import React, { useState, useEffect, useMemo, useCallback } from 'react';
import cpvTreeData from '../cpv_tree.json';
import { ChevronRight, ChevronDown, Check, Minus, Search, X, Tag } from 'lucide-react';

/**
 * Componente per la selezione gerarchica dei codici CPV (Common Procurement Vocabulary).
 * Permette di selezionare categorie e sottocategorie, propagando la selezione ai figli.
 */
const CPVTreeSelector = ({ 
  selectedCodes = [], 
  onCodesChange,
  maxHeight = '400px',
  className = '',
  forceExpanded = false,
  disabled = false
}) => {
  const [expandedNodes, setExpandedNodes] = useState(new Set());
  // Debounce della ricerca per evitare filtro/expand ad ogni singolo keypress
  const [searchInput, setSearchInput] = useState('');
  const [searchTerm, setSearchTerm] = useState('');
  const [isExpanded, setIsExpanded] = useState(false);

  // Permette al parent (es. modal guest) di forzare l'espansione
  useEffect(() => {
    if (forceExpanded) setIsExpanded(true);
  }, [forceExpanded]);

  // Lookup O(1) invece di array.includes (molto più veloce con tante selezioni)
  const selectedCodesSet = useMemo(() => new Set(selectedCodes), [selectedCodes]);

  useEffect(() => {
    const t = setTimeout(() => setSearchTerm(searchInput), 200);
    return () => clearTimeout(t);
  }, [searchInput]);

  // Funzione per ottenere tutti i codici figli di un nodo (ricorsivo)
  const getAllChildCodes = useCallback((node) => {
    const codes = [];
    
    // Aggiungi il codice del nodo corrente (usa full_code se disponibile)
    const nodeCode = node.full_code || node.code;
    if (nodeCode) {
      // Estrai solo la parte numerica prima del trattino
      const cleanCode = nodeCode.split('-')[0].replace('.0', '');
      codes.push(cleanCode);
    }
    
    // Aggiungi ricorsivamente i codici dei figli
    if (node.children && node.children.length > 0) {
      node.children.forEach(child => {
        codes.push(...getAllChildCodes(child));
      });
    }
    
    return codes;
  }, []);

  // Funzione per verificare se TUTTI i figli sono selezionati (ricorsivo)
  const areAllChildrenSelected = useCallback((node) => {
    const nodeCode = (node.full_code || node.code).split('-')[0].replace('.0', '');
    
    // Se non ha figli, verifica se il nodo stesso è selezionato
    if (!node.children || node.children.length === 0) {
      return selectedCodesSet.has(nodeCode);
    }
    
    // Ha figli: verifica che il nodo E tutti i figli siano selezionati
    const nodeSelected = selectedCodesSet.has(nodeCode);
    const allChildrenSelected = node.children.every(child => areAllChildrenSelected(child));
    
    return nodeSelected && allChildrenSelected;
  }, [selectedCodesSet]);

  // Funzione per verificare se ALCUNI figli sono selezionati (stato indeterminato)
  const areSomeChildrenSelected = useCallback((node) => {
    const nodeCode = (node.full_code || node.code).split('-')[0].replace('.0', '');
    
    // Se non ha figli, verifica se il nodo stesso è selezionato
    if (!node.children || node.children.length === 0) {
      return selectedCodesSet.has(nodeCode);
    }
    
    // Ha figli: verifica se almeno uno è selezionato
    const nodeSelected = selectedCodesSet.has(nodeCode);
    const someChildrenSelected = node.children.some(child => areSomeChildrenSelected(child));
    
    return nodeSelected || someChildrenSelected;
  }, [selectedCodesSet]);

  // Gestisce il toggle di un nodo - SELEZIONA A CASCATA TUTTI I FIGLI
  const handleNodeToggle = useCallback((node) => {
    const allChildCodes = getAllChildCodes(node);
    
    // Se il nodo o tutti i suoi figli sono già selezionati, deseleziona TUTTI
    if (areAllChildrenSelected(node)) {
      const newCodes = selectedCodes.filter(code => !allChildCodes.includes(code));
      onCodesChange(newCodes);
    } else {
      // Altrimenti, seleziona il nodo E TUTTI I SUOI FIGLI a cascata
      const newCodes = [...new Set([...selectedCodes, ...allChildCodes])];
      onCodesChange(newCodes);
    }
  }, [selectedCodes, onCodesChange, getAllChildCodes, areAllChildrenSelected]);

  // Toggle espansione nodo
  const toggleExpand = useCallback((nodeCode) => {
    setExpandedNodes(prev => {
      const newSet = new Set(prev);
      if (newSet.has(nodeCode)) {
        newSet.delete(nodeCode);
      } else {
        newSet.add(nodeCode);
      }
      return newSet;
    });
  }, []);

  // Filtra l'albero in base al termine di ricerca
  const filterTree = useCallback((nodes, term) => {
    if (!term) return nodes;
    
    const lowerTerm = term.toLowerCase();
    
    return nodes.reduce((filtered, node) => {
      const matchesLabel = node.label.toLowerCase().includes(lowerTerm);
      const matchesCode = node.code.includes(term) || (node.full_code && node.full_code.includes(term));
      
      const filteredChildren = node.children ? filterTree(node.children, term) : [];
      
      if (matchesLabel || matchesCode || filteredChildren.length > 0) {
        filtered.push({
          ...node,
          children: filteredChildren.length > 0 ? filteredChildren : node.children
        });
      }
      
      return filtered;
    }, []);
  }, []);

  // Albero filtrato
  const filteredTree = useMemo(() => {
    return filterTree(cpvTreeData, searchTerm);
  }, [searchTerm, filterTree]);

  // Espandi automaticamente quando si cerca
  useEffect(() => {
    if (searchTerm) {
      // Evita di espandere TUTTO l'albero (costoso): espandi solo i primi livelli.
      const allCodes = new Set();
      const collectCodes = (nodes, depth = 0) => {
        nodes.forEach(node => {
          allCodes.add(node.code);
          if (node.children && depth < 2) collectCodes(node.children, depth + 1);
        });
      };
      collectCodes(filteredTree);
      setExpandedNodes(allCodes);
    }
  }, [searchTerm, filteredTree]);

  // Renderizza un nodo dell'albero
  const renderNode = useCallback((node, depth = 0) => {
    const nodeCode = node.code;
    const hasChildren = node.children && node.children.length > 0;
    const isExpanded = expandedNodes.has(nodeCode);
    const allSelected = areAllChildrenSelected(node);
    const someSelected = areSomeChildrenSelected(node);
    
    // Colori per livello
    const levelColors = {
      'Divisione': 'bg-blue-100 text-blue-800 border-blue-300',
      'Gruppo': 'bg-green-100 text-green-800 border-green-300',
      'Classe': 'bg-yellow-100 text-yellow-800 border-yellow-300',
      'Categoria': 'bg-purple-100 text-purple-800 border-purple-300',
      'Prodotto finale': 'bg-gray-100 text-gray-800 border-gray-300'
    };
    
    const levelColor = levelColors[node.level] || 'bg-gray-100 text-gray-700';
    
    return (
      <div key={nodeCode} className="select-none">
        <div 
          className={`flex items-center py-1.5 px-2 hover:bg-gray-50 rounded cursor-pointer transition-colors ${
            allSelected ? 'bg-blue-50' : ''
          }`}
          style={{ paddingLeft: `${depth * 20 + 8}px` }}
        >
          {/* Expand/Collapse button */}
          <button
            type="button"
            onClick={(e) => {
              e.stopPropagation();
              toggleExpand(nodeCode);
            }}
            className="w-5 h-5 flex items-center justify-center mr-1 text-gray-500 hover:text-gray-700"
          >
            {hasChildren ? (
              isExpanded ? <ChevronDown size={16} /> : <ChevronRight size={16} />
            ) : (
              <span className="w-4" />
            )}
          </button>
          
          {/* Checkbox */}
          <button
            type="button"
            onClick={(e) => {
              e.stopPropagation();
              handleNodeToggle(node);
            }}
            className={`w-5 h-5 flex items-center justify-center mr-2 rounded border ${
              allSelected 
                ? 'bg-blue-600 border-blue-600 text-white' 
                : someSelected 
                  ? 'bg-blue-200 border-blue-400 text-blue-600'
                  : 'border-gray-300 hover:border-blue-400'
            }`}
          >
            {allSelected ? (
              <Check size={14} />
            ) : someSelected ? (
              <Minus size={14} />
            ) : null}
          </button>
          
          {/* Label */}
          <div 
            className="flex-1 flex items-center gap-2 min-w-0"
            onClick={() => hasChildren && toggleExpand(nodeCode)}
          >
            <span className={`text-xs px-1.5 py-0.5 rounded border ${levelColor}`}>
              {node.code.split('.')[0]}
            </span>
            <span className="text-sm text-gray-700 truncate" title={node.label}>
              {node.label}
            </span>
          </div>
        </div>
        
        {/* Children */}
        {hasChildren && isExpanded && (
          <div className="ml-2">
            {node.children.map(child => renderNode(child, depth + 1))}
          </div>
        )}
      </div>
    );
  }, [expandedNodes, toggleExpand, handleNodeToggle, areAllChildrenSelected, areSomeChildrenSelected]);

  return (
    <div className={`cpv-tree-selector ${className} ${disabled ? 'opacity-75' : ''}`}>
      {/* Header con toggle */}
      <div 
        className="flex items-center justify-between p-3 bg-gradient-to-r from-indigo-50 to-purple-50 border border-indigo-200 rounded-lg cursor-pointer hover:bg-indigo-100 transition-colors"
        onClick={() => {
          if (disabled) return;
          if (!forceExpanded) setIsExpanded(!isExpanded);
        }}
      >
        <div className="flex items-center gap-2">
          <Tag className="h-5 w-5 text-indigo-600" />
          <span className="font-medium text-gray-800">Filtro per CPV</span>
          {selectedCodes.length > 0 && (
            <span className="bg-indigo-600 text-white text-xs px-2 py-0.5 rounded-full">
              {selectedCodes.length} selezionati
            </span>
          )}
        </div>
        <ChevronDown 
          className={`h-5 w-5 text-gray-500 transition-transform ${isExpanded ? 'rotate-180' : ''}`} 
        />
      </div>

      {/* Contenuto espandibile */}
      {isExpanded && (
        <div className={`mt-2 border border-gray-200 rounded-lg bg-white shadow-sm ${disabled ? 'pointer-events-none select-none' : ''}`}>
          {/* Barra di ricerca */}
          <div className="p-3 border-b border-gray-200">
            <div className="relative">
              <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 h-4 w-4 text-gray-400" />
              <input
                type="text"
                placeholder="Cerca categoria o codice CPV..."
                value={searchInput}
                onChange={(e) => setSearchInput(e.target.value)}
                disabled={disabled}
                className="w-full pl-10 pr-4 py-2 border border-gray-300 rounded-lg text-sm focus:ring-2 focus:ring-indigo-500 focus:border-transparent"
              />
              {searchInput && (
                <button
                  type="button"
                  onClick={() => setSearchInput('')}
                  className="absolute right-3 top-1/2 transform -translate-y-1/2 text-gray-400 hover:text-gray-600"
                >
                  <X size={16} />
                </button>
              )}
            </div>
          </div>

          {/* Albero CPV */}
          <div 
            className="overflow-y-auto p-2"
            style={{ maxHeight }}
          >
            {filteredTree.length === 0 ? (
              <div className="text-center py-8 text-gray-500">
                <Tag className="h-8 w-8 mx-auto mb-2 opacity-50" />
                <p>Nessuna categoria trovata</p>
              </div>
            ) : (
              filteredTree.map(node => renderNode(node))
            )}
          </div>

          {/* Legenda */}
          <div className="p-3 border-t border-gray-200 bg-gray-50">
            <div className="text-xs text-gray-500 mb-2">Legenda livelli:</div>
            <div className="flex flex-wrap gap-2 text-xs">
              <span className="px-2 py-0.5 rounded bg-blue-100 text-blue-800 border border-blue-300">Divisione</span>
              <span className="px-2 py-0.5 rounded bg-green-100 text-green-800 border border-green-300">Gruppo</span>
              <span className="px-2 py-0.5 rounded bg-yellow-100 text-yellow-800 border border-yellow-300">Classe</span>
              <span className="px-2 py-0.5 rounded bg-purple-100 text-purple-800 border border-purple-300">Categoria</span>
              <span className="px-2 py-0.5 rounded bg-gray-100 text-gray-800 border border-gray-300">Prodotto</span>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default CPVTreeSelector;

